import { Box, Checkbox, Drawer, FormControlLabel, IconButton, Radio, useMediaQuery } from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import CloseIcon from "assets/CloseIcon";
import { ReactComponent as Filter } from "assets/filter.svg";
import { ReactComponent as FilterIconBlueMobile } from "assets/filter_icon_mobile.svg";
import { ReactComponent as FilterIconBlue } from "assets/FilterIcon.svg";
import { useTranslate } from "hooks/useTranslate";
import { cloneDeep, isEmpty, uniqueId } from "lodash";
import * as React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";

import { updateBookmarkedInncodesAsync } from "components/features/dashboard/Dashboard.slice";
import {
  accessTypes,
  corpFilterConstants,
  corpFilterTypeConst,
  DEFAULT_CORP_INNCODE,
  minSearchStrLength,
  optionTypeArray,
  planDetailsMetaKeyMap,
  sourceList
} from "utils/CommonConstants";
import { getGlobalLevelFilterCount, useDebouncedEffect } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { theme as MUITheme } from "../../../styles/Theme";
import Button from "../Button";
import CheckBox from "../Checkbox";
import CollapsibleContainer from "../CollapsibleContainer";
import DateRangePickerCalendar from "../DateRangePickerCalendar";
import ButtonLink from "../Link";
import SearchAppBar from "../searchBar";
import Typography from "../Typography";
import {
  fetchFilterPropertyInfo,
  fetchFilterSelectedPlansAsync,
  fetchManagementcOInfo,
  fetchmembersInfo,
  fetchOwnerInfo,
  fetchPropertylist,
  fetchUserSearchedInncodeAsync,
  searchOwners,
  searchUserAssociatedInncodeLocally,
  setCheckedDynamicInncode,
  setCheckedOwnerInfo,
  setCheckedPlanInfo,
  setMemberInfo,
  setSearchText,
  setStaticPropertyInfo
} from "./Filters.slice";
import { getDynamicFiltersOptions, shouldShowFilterCallback } from "./FiltersFunctions";
import { filterPage, optionsTypes } from "./FiltersHelper";
import { resetPageModifiedFilter, setPageFilter } from "./GlobalFilters.slice";
import "./index.scss";
import InncodeFilterBookmark from "./InncodeFilterBookmark";

interface Props {
  page?: string;
  isDefaultButtonsApplicable?: boolean;
  updateSavedFilter?: any;
  isSearchBarVisible?: boolean;
  searchBarPlaceholder?: string;
  getMembersList?: any;
  ownerDetails?: any;
  innCode?: string;
  functionalAreaId?: string;
  applyFilterToMultiplePages?: string[];
  isGlobalCheckBoxApplicable?: boolean;
  isPreDefinedNav?: boolean;
  searchedText?: string;
  filterButtonName?: string;
  setRecentSearch?: any;
  libraryRecentSearch?: any;
}

const GlobalFilters = React.memo((props: Props) => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const [searchParams] = useSearchParams();

  const {
    page = "",
    isDefaultButtonsApplicable = false,
    updateSavedFilter,
    isSearchBarVisible,
    searchBarPlaceholder = "Search by Plan name",
    getMembersList,
    ownerDetails,
    innCode = DEFAULT_CORP_INNCODE,
    functionalAreaId = "",
    applyFilterToMultiplePages,
    isGlobalCheckBoxApplicable = false,
    isPreDefinedNav = false,
    searchedText = "",
    filterButtonName = "Filters",
    setRecentSearch
  } = props;
  const searchText = searchParams.get("search") || searchedText;

  const filterCategories = useAppSelector((state) => state.filters.filterCategories);
  const searchedInncodes = useAppSelector((state) => state.filters.searchedInncodes);
  const plansList = useAppSelector((state) => state.filters.searchPlanList);
  const managementList = useAppSelector((state) => state.filters.searchManagements);
  const ownerGroupList = useAppSelector((state) => state.filters.searchOwnersGroup);
  const membersList = useAppSelector((state) => state.filters.searchMembersList);
  const ownerList = useAppSelector((state) => state.filters.searchOwners);
  const propertyList = useAppSelector((state) => state.filters.searchPropertyList);
  const staticPropertyInfo = useAppSelector((state) => state.filters.staticPropertyInfo);
  const propertyInfo = useAppSelector((state) => state.filters.propertyList);
  const planInfo = useAppSelector((state) => state.filters.planInfo);
  const ownerInfo = useAppSelector((state) => state.filters.ownerInfo);
  const memberInfo = useAppSelector((state) => state.filters.memberInfo);
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const usersList = useAppSelector((state) => state.aboveProperty.roleCategoryList);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isImpersonated = useAppSelector((state) => state.app.impersonationDetails.isImpersonated);
  const bookmarkedInncodes = useAppSelector((state) => state.Dashboard.bookMarkInncodes);
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[page]);
  const selectedPlanDetail: any = useAppSelector((state) => state.planDetails.planDetails);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isGlobalCheckBoxSelect, setIsGlobalCheckBoxSelect] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [checkedList, setCheckedList] = useState<any>([]);
  const [innCodeSearchValue, setInnCodeSearchValue] = useState("");
  const [planSearchValue, setPlanSearchValue] = useState("");
  const [managementSearchValue, setManagementSearchValue] = useState("");
  const [ownerGroupSearchValue, setOwnerGroupSearchValue] = useState("");
  const [ownerSearchValue, setOwnerSearchValue] = useState("");
  const [memberSearchValue, setMemberSearchValue] = useState("");
  const [propertySearchValue, setPropertySearchValue] = useState("");
  const [disableFields, setDisableFields] = useState(false);
  const [bookmarked, setBookmarked] = useState<string[]>(bookmarkedInncodes);
  const selectedFilter = isGlobalCheckBoxApplicable
    ? globalFiltersforPage?.isFilterApplied
      ? globalFiltersforPage?.modifiedFilter
      : globalFiltersforPage?.defaultFilter
    : (isEmpty(globalFiltersforPage?.modifiedFilter)
        ? globalFiltersforPage?.defaultFilter
        : globalFiltersforPage?.modifiedFilter) || [];

  useEffect(() => {
    const selectedFilters = selectedFilter;
    if (page === filterPage.libraryPlanDetailsDisplay && selectedFilter.some((obj: any) => obj.key === "innCode"))
      setDisableFields(true);
    setCheckedList(selectedFilters);
  }, [globalFiltersforPage]);

  useEffect(() => {
    const { totalSelectableOptions, checkedOptionsCount, totalCategoryForPage } = filterCategories?.reduce(
      (acc: any, category: any) => {
        if (category[page] === "Y") {
          acc.totalSelectableOptions += category?.filters?.length || 0;
          const selectedCount = getChildernSelectedCount(category) ?? "";
          acc.checkedOptionsCount += parseInt(selectedCount.replace(/\D/g, ""), 10) || 0;
          acc.totalCategoryForPage += 1;
        }
        return acc;
      },
      { totalSelectableOptions: 0, checkedOptionsCount: 0, totalCategoryForPage: 0 }
    );
    setIsGlobalCheckBoxSelect(
      totalCategoryForPage === checkedList?.length && totalSelectableOptions === checkedOptionsCount
    );
  }, [checkedList]);

  const DynamicViewByType: any = {
    value: {
      inncode: innCodeSearchValue,
      innCode: innCodeSearchValue,
      planCmsId: planSearchValue,
      ids: planSearchValue,
      managementCompany: managementSearchValue,
      ownerGroup: ownerGroupSearchValue,
      planOwner: ownerSearchValue,
      hltIds: memberSearchValue,
      searchedHltId: memberSearchValue,
      cmsId: planSearchValue,
      hltId: memberSearchValue,
      staticListIds: propertySearchValue,
      users: memberSearchValue,
      filteredInnCodes: innCodeSearchValue,
      assignedBy: memberSearchValue
    },
    change: {
      inncode: setInnCodeSearchValue,
      innCode: setInnCodeSearchValue,
      planCmsId: setPlanSearchValue,
      ids: setPlanSearchValue,
      managementCompany: setManagementSearchValue,
      ownerGroup: setOwnerGroupSearchValue,
      planOwner: setOwnerSearchValue,
      hltIds: setMemberSearchValue,
      cmsId: setPlanSearchValue,
      hltId: setMemberSearchValue,
      searchedHltId: setMemberSearchValue,
      staticListIds: setPropertySearchValue,
      users: setMemberSearchValue,
      filteredInnCodes: setInnCodeSearchValue,
      assignedBy: setMemberSearchValue
    },
    result: {
      inncode: searchedInncodes,
      innCode: searchedInncodes,
      planCmsId: plansList,
      ids: plansList,
      managementCompany: managementList,
      ownerGroup: ownerGroupList,
      planOwner: ownerList,
      hltIds: membersList,
      cmsId: plansList,
      hltId: membersList,
      filteredInnCodes: searchedInncodes,
      searchedHltId: membersList,
      staticListIds: propertyList,
      users: membersList,
      assignedBy: membersList
    }
  };

  useDebouncedEffect(
    () => {
      if (innCodeSearchValue && innCodeSearchValue.length > minSearchStrLength)
        if (isCorp) dispatch(fetchUserSearchedInncodeAsync(innCodeSearchValue));
        else dispatch(searchUserAssociatedInncodeLocally({ searchText: innCodeSearchValue }));
    },
    [innCodeSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (managementSearchValue && managementSearchValue.length > minSearchStrLength)
        dispatch(fetchManagementcOInfo(managementSearchValue));
    },
    [managementSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (ownerGroupSearchValue && ownerGroupSearchValue.length > minSearchStrLength)
        dispatch(fetchOwnerInfo(ownerGroupSearchValue));
    },
    [ownerGroupSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (planSearchValue && planSearchValue.length > minSearchStrLength) {
        dispatch(
          fetchFilterSelectedPlansAsync({
            payload: { searchText: planSearchValue, filter: { innCode } },
            isSearched: true
          })
        );
      }
    },
    [planSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (ownerSearchValue && ownerSearchValue.length > minSearchStrLength) {
        dispatch(searchOwners({ userList: ownerDetails || usersList, searchText: ownerSearchValue }));
      }
    },
    [ownerSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (memberSearchValue && memberSearchValue.length > minSearchStrLength) {
        getMembersList(memberSearchValue);
      }
    },
    [memberSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (propertySearchValue && propertySearchValue.length > minSearchStrLength) {
        dispatch(
          fetchPropertylist({ types: "static", searchFilterText: propertySearchValue, fetchFilterOptions: false })
        );
      }
    },
    [propertySearchValue],
    500
  );

  const toggleInput = React.useCallback(
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent | React.SyntheticEvent<Element, Event>) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpenDrawer(open);
    },
    [setOpenDrawer]
  );

  const handleChange = (panel: string, key: string) => {
    let newArr = [...expanded];
    const position = expanded.indexOf(panel);
    if (position > -1) newArr.splice(position, 1);
    setExpanded(position > -1 ? newArr : [...expanded, panel]);

    if (getKeyCheck(key, corpFilterTypeConst.innCode)) checkAndGetInncodeDetails();
    else if (getKeyCheck(key, corpFilterTypeConst.planName)) checkAndGetPlanDetails();
    else if (getKeyCheck(key, corpFilterTypeConst.owners)) checkAndGetOwnerDetails();
    else if (getKeyCheck(key, corpFilterTypeConst.property)) checkAndGetPropertyDetails();
  };

  const getKeyCheck = (parentKey: string, type: string) => {
    return corpFilterConstants[type]?.includes(parentKey);
  };

  const checkAndGetOwnerDetails = () => {
    const options = checkedList.find((item: any) => getKeyCheck(item?.key, corpFilterTypeConst.owners))?.options;
    const filtered = options?.filter((item: string) => ownerInfo?.findIndex((prop: any) => prop.id === item) < 0);
    if (filtered?.length) {
      const props: any = {
        hltIds: filtered,
        page: 0,
        size: filtered?.length,
        type: "owner"
      };
      dispatch(fetchmembersInfo(props));
    }
  };

  const checkAndGetPropertyDetails = () => {
    const options = checkedList.find((item: any) => getKeyCheck(item?.key, corpFilterTypeConst.property))?.options;
    const filtered = options?.filter(
      (item: string) => staticPropertyInfo?.findIndex((prop: any) => prop.id === item) < 0
    );
    if (filtered?.length)
      dispatch(
        fetchPropertylist({
          types: "static",
          filterIds: filtered,
          fetchFilterOptions: false,
          searchFilterText: "",
          fetchIdToValue: true
        })
      );
  };

  const checkAndGetInncodeDetails = () => {
    const options = checkedList.find((item: any) => getKeyCheck(item?.key, corpFilterTypeConst.innCode))?.options;
    const filtered = options?.filter(
      (item: string) => propertyInfo?.findIndex((prop: any) => prop.innCode === item) < 0
    );
    if (filtered?.length) dispatch(fetchFilterPropertyInfo({ innCodesValue: filtered }));
  };

  const checkAndGetPlanDetails = () => {
    const options = checkedList.find((item: any) => getKeyCheck(item?.key, corpFilterTypeConst.planName))?.options;
    const filtered = options?.filter((item: string) => planInfo?.findIndex((prop: any) => prop.id === item) < 0);
    if (filtered?.length)
      dispatch(
        fetchFilterSelectedPlansAsync({
          payload: {
            filter: {
              ids: filtered
            }
          }
        })
      );
  };

  const getChildernSelectedCount = (item: any) => {
    const options = checkedList.find((checkedList: any) => checkedList.key === item.key)?.options;
    let count: number = 0;
    if (item?.optionsType === "key-array-value-array" && options?.length) {
      sourceList?.map((item: any) => {
        if (options?.includes(item.value[0])) count++;
      });
    } else count = options?.length;
    if (count) return "(" + count + ")";
  };

  const globalAndParentSelectAll = (event: any, isGlobalSelectAll: boolean, parent?: any) => {
    const checked = event.target.checked;
    let newCheckedList = cloneDeep(checkedList);
    const foundItemIndex = newCheckedList.findIndex((item: any) => item.key === parent?.key);
    if (isGlobalSelectAll) {
      if (checked) {
        newCheckedList = filterCategories
          ?.filter((fil: any) => fil[page] === "Y")
          ?.map((item: any) => {
            return {
              id: item?.id,
              options: getParentCheckboxOptions(item) || [],
              key: item?.key,
              name: item?.name
            };
          });
        setIsGlobalCheckBoxSelect(true);
      } else {
        newCheckedList = [];
        setIsGlobalCheckBoxSelect(false);
      }
      setCheckedList(newCheckedList);
    } else {
      if (checked) {
        const options = getParentCheckboxOptions(parent);
        if (foundItemIndex !== -1) {
          newCheckedList[foundItemIndex].options = options || [];
        } else {
          newCheckedList.push({
            id: parent?.id,
            options: options || [],
            key: parent?.key,
            name: parent?.name
          });
        }
      } else {
        newCheckedList.splice(foundItemIndex, 1);
      }
      setCheckedList(newCheckedList);
    }
  };

  const getParentCheckboxOptions = (parent: any) => {
    return parent?.filters?.flatMap((item: any) => {
      if (parent.optionsType === "array") return item;
      else return item?.key;
    });
  };

  const getCheckedStatusForParent = (isDynamic: boolean, item: any) => {
    const checkedOptions = checkedList?.find((value: any) => value?.key === item.key)?.options;
    if (item?.optionsType === "key-array-value-array")
      return (
        sourceList?.filter((item: any) => checkedOptions?.includes(item.value[0])).length === item?.filters?.length
      );
    else return checkedOptions?.length === (item?.filters || [])?.length;
  };

  const saveAsDefaultApplicable = () => {
    return globalFiltersforPage?.modifiedFilter.map((obj: any) => obj.options?.length > 0).includes(true);
  };

  const isCountVisibleOnButton = (selectedFilter: any) => {
    return selectedFilter?.map((obj: any) => obj.options?.length > 0).includes(true);
  };

  const checkForFilterChangedFromModified = (modifiedFilter: any) => {
    return JSON.stringify(globalFiltersforPage?.defaultFilter) === JSON.stringify(modifiedFilter);
  };

  const getParentCheckBoxes = () => {
    return (
      <>
        {isGlobalCheckBoxApplicable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "0px 15px",
              color: tokens.Text.General.PrimaryHeadline
            }}
          >
            <CheckBox
              sx={{ marginRight: "-8px" }}
              id={uniqueId("OPTIONS2_")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => globalAndParentSelectAll(e, true)}
              checked={isGlobalCheckBoxSelect}
            />
            <Typography variant="body1">{"Select All"}</Typography>
          </Box>
        )}
        {filterCategories?.map((item: any) => {
          if (item[page] === "Y" || (item[page] === "C" && shouldShowFilterCallback(page))) {
            const isChecked = getCheckedStatusForParent(item?.isDynamic === "Y", item);
            return (
              <React.Fragment key={item.id}>
                <Box
                  sx={{
                    ...{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 24px",
                      color: tokens.Text.General.PrimaryHeadline
                    },
                    ...{}
                  }}
                >
                  <Box display={"flex"} flexDirection={"row"} gap="8px" alignItems={"center"}>
                    {isGlobalCheckBoxApplicable && (
                      <CheckBox
                        sx={{ marginX: "-8px" }}
                        id={uniqueId("OPTIONS2_")}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => globalAndParentSelectAll(e, false, item)}
                        checked={isChecked}
                      />
                    )}
                    <Typography sx={{ marginLeft: "-8px" }} variant="body1" showBold>
                      {item.name}
                    </Typography>
                    {isChecked && item?.isDynamic === "Y" && <Typography variant="body1">{"(All)"}</Typography>}
                    <Typography variant="body1">{getChildernSelectedCount(item)}</Typography>
                  </Box>

                  <IconButton aria-label="expand" onClick={() => handleChange(item.id, item.key)}>
                    {expanded.indexOf(item.id) > -1 ? <ArrowUp /> : <ArrowDown />}
                  </IconButton>
                </Box>

                <CollapsibleContainer
                  style={{ maxWidth: isMobileScreen ? "100%" : "350px" }}
                  isOpen={expanded.indexOf(item.id) > -1}
                >
                  {getChildrenCheckboxes(item, isChecked)}
                  {(checkedList?.find((value: any) => value?.key === item.key)?.options?.length > 0 || isChecked) && (
                    <Typography
                      variant="body2"
                      showBold
                      color={tokens.Text.General.Color}
                      sx={{
                        paddingX: item?.isDynamic === "Y" && isChecked ? "30px" : "50px",
                        paddingY: "20px",
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        if (
                          page === filterPage.libraryPlanDetailsDisplay &&
                          getKeyCheck(item.key, corpFilterTypeConst.innCode)
                        )
                          setDisableFields(false);
                        setCheckedList((prevList: any) => prevList.filter((value: any) => value.key !== item.key));
                      }}
                    >
                      Clear Filter
                    </Typography>
                  )}
                </CollapsibleContainer>
              </React.Fragment>
            );
          }
        })}
      </>
    );
  };

  const getParentOptions = (isInncodeType: boolean, item: any, parent: any) => {
    let options;
    if (isInncodeType) {
      options = item?.innCode;
    } else if (
      getKeyCheck(parent?.key, corpFilterTypeConst.management) ||
      getKeyCheck(parent?.key, corpFilterTypeConst.ownerGroup)
    ) {
      options = item;
    } else {
      options = item?.id || item?.hltId;
    }
    return options;
  };

  const getOptionLabel = (item: any, isInncodeType: boolean, isMemberType: boolean) => {
    return (
      <>
        {!isInncodeType && (
          <Typography variant="body2">
            {item?.name ||
              item?.displayName ||
              (item?.firstName || item?.lastName
                ? item?.firstName && item.lastName
                  ? `${item?.firstName} ${item.lastName}`
                  : item?.firstName || item.lastName
                : item?.email
                ? ""
                : item)}
          </Typography>
        )}
        {isInncodeType && (
          <>
            <Typography variant="body2">{item?.name}</Typography>
            <Typography variant="body2">{item?.innCode}</Typography>
          </>
        )}
        {isMemberType && <Typography variant="body2">{item?.email}</Typography>}
      </>
    );
  };

  const getSelectedOptionLabel = (
    item: any,
    isInncodeType: boolean,
    isMemberType: boolean,
    isOwnerType: boolean,
    parent: any
  ) => {
    return (
      <>
        <Typography variant="body2">{getUserViewName(isInncodeType, item, parent.key)}</Typography>
        {isInncodeType && <Typography variant="body2">{item}</Typography>}
        {isMemberType && <Typography variant="body2">{getMemberEmail(item)}</Typography>}
        {isOwnerType && <Typography variant="body2">{getMemberEmailForOwners(item)}</Typography>}
      </>
    );
  };

  const getDynamicSingleSelectOptions = (
    item: any,
    parent: any,
    isChecked: boolean,
    isInncodeType: boolean,
    isMemberType: boolean
  ) => {
    return (
      <Box
        sx={{
          width: "100%",
          cursor: "pointer",
          backgroundColor: isChecked ? tokens.Background.General.Color : "inherit",
          color: isChecked ? tokens.Text.General.Color : "inherit"
        }}
        id={uniqueId("OPTIONS2_")}
        onClick={(e: any) =>
          handleChildrenChange(e, {
            parentId: parent.id,
            parentKey: parent.key,
            optionsType: parent.optionsType,
            option: getParentOptions(isInncodeType, item, parent),
            name: item?.name || getUserNameWithConditionalCheck(item?.displayName, item?.firstName, item?.lastName),
            parentName: parent.name,
            email: item?.email
          })
        }
      >
        <Typography variant="label" color={isChecked ? tokens.Text.General.Color : tokens.Text.General.Secondary}>
          {getOptionLabel(item, isInncodeType, isMemberType)}
        </Typography>
      </Box>
    );
  };

  const getDynamicCategoryResult = (
    isInncodeType: boolean,
    isMemberType: boolean,
    inputValue: any,
    parent: any,
    results: any
  ) => {
    if (inputValue && !results?.length && getKeyCheck(parent?.key, corpFilterTypeConst.owners))
      return (
        <Typography
          sx={{ paddingX: "15px" }}
          variant={"label"}
          children={translate("common.memberNotFound")}
        ></Typography>
      );
    else
      return (
        inputValue &&
        results?.map((item: any) => {
          const isCheckedFunc = () => {
            if (checkedList.length === 0) {
              return false;
            }
            const parentCheckedItem = checkedList.find((checkedItem: any) => checkedItem.key === parent.key);
            if (parentCheckedItem) {
              const options = getParentOptions(isInncodeType, item, parent);
              const optionIndex = parentCheckedItem.options.indexOf(options);
              return optionIndex > -1;
            }

            return false;
          };
          const isChecked = isCheckedFunc();
          return (
            <Box
              key={uniqueId("OPTIONS_")}
              sx={{
                ...{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingLeft: "25px",
                  color: tokens.Text.General.PrimaryHeadline
                },
                ...{}
              }}
            >
              {parent.optionsType !== optionsTypes["dynamic-single-select"] ? (
                <FormControlLabel
                  label={getOptionLabel(item, isInncodeType, isMemberType)}
                  control={
                    <CheckBox
                      id={uniqueId("OPTIONS2_")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChildrenChange(e, {
                          parentId: parent.id,
                          parentKey: parent.key,
                          option: getParentOptions(isInncodeType, item, parent),
                          name:
                            item?.name ||
                            getUserNameWithConditionalCheck(item?.displayName, item?.firstName, item?.lastName),
                          parentName: parent.name,
                          email: item?.email
                        })
                      }
                      checked={isChecked}
                    />
                  }
                />
              ) : (
                getDynamicSingleSelectOptions(item, parent, isChecked, isInncodeType, isMemberType)
              )}
              {page === filterPage.consultantInncodePlansDisplay && isInncodeType ? (
                <InncodeFilterBookmark bookmarked={bookmarked} setBookmarked={setBookmarked} item={item} />
              ) : (
                <></>
              )}
            </Box>
          );
        })
      );
  };

  const getUserViewName = (isInncodeType: boolean, value: any, parentKey: string) => {
    if (isInncodeType) return propertyInfo?.find((item: any) => item?.innCode === value)?.propName;
    else if (
      getKeyCheck(parentKey, corpFilterTypeConst.management) ||
      getKeyCheck(parentKey, corpFilterTypeConst.ownerGroup)
    )
      return value;
    else if (getKeyCheck(parentKey, corpFilterTypeConst.owners))
      return ownerInfo?.find((item: any) => item?.id === value)?.name;
    else if (getKeyCheck(parentKey, corpFilterTypeConst.members)) {
      const member = memberInfo?.find((item: any) => item?.hltId === value);
      return getUserNameWithConditionalCheck(member?.displayName, member?.firstName, member?.lastName);
    } else if (getKeyCheck(parentKey, corpFilterTypeConst.property)) {
      const property = staticPropertyInfo?.find((item: any) => item?.id === value)?.name;
      return property;
    } else return planInfo?.find((item: any) => item?.id === value)?.name;
  };

  const getMemberEmail = (hltId: string) =>
    membersList?.find((i: any) => i?.hltId === hltId)?.email || memberInfo?.find((i: any) => i?.hltId === hltId)?.email;

  const getMemberEmailForOwners = (hltId: string) => {
    return ownerInfo?.find((item: any) => item?.id === hltId)?.email;
  };

  const getSelectedDynamicCategoryResult = (
    isInncodeType: boolean,
    isMemberType: boolean,
    isOwnerType: boolean,
    parent: any
  ) => {
    return checkedList
      ?.find((item: any) => item?.key === parent.key)
      ?.options?.map((item: any) => {
        return (
          <Box
            key={item}
            sx={{
              ...{
                display: "flex",
                paddingLeft: "25px",
                color: tokens.Text.General.PrimaryHeadline,
                justifyContent: "space-between"
              },
              ...{}
            }}
          >
            {parent.optionsType !== optionsTypes["dynamic-single-select"] ? (
              <FormControlLabel
                label={getSelectedOptionLabel(item, isInncodeType, isMemberType, isOwnerType, parent)}
                control={
                  <CheckBox
                    id={item}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChildrenChange(e, {
                        parentId: parent.key,
                        parentKey: parent.key,
                        option: item
                      })
                    }
                    checked={true}
                  />
                }
              />
            ) : (
              <Box
                display={"flex"}
                flexDirection={"column"}
                sx={{
                  backgroundColor: tokens.Background.General.Color,
                  color: tokens.Text.General.Color,
                  width: "100%"
                }}
              >
                {getSelectedOptionLabel(item, isInncodeType, isMemberType, isOwnerType, parent)}
              </Box>
            )}
            {page === filterPage.consultantInncodePlansDisplay && isInncodeType ? (
              <InncodeFilterBookmark bookmarked={bookmarked} setBookmarked={setBookmarked} item={{ innCode: item }} />
            ) : (
              <></>
            )}
          </Box>
        );
      });
  };

  const getChildernValueToCheckIsChecked = (optionType: string, subFilter: any) => {
    if (optionTypeArray.includes(optionType)) return Array.isArray(subFilter?.key) ? subFilter?.key[0] : subFilter?.key;
    else return subFilter;
  };

  const getDynamicView = (parentKey: string, type: string) => {
    return DynamicViewByType[type][parentKey];
  };

  const getFiltersByViewType = (filters: any[], parent: any) => {
    if (page !== filterPage.libraryPlanDetailsDisplay) return filters;
    const keyMatch = planDetailsMetaKeyMap[parent?.key];
    const selectedValues = selectedPlanDetail?.meta?.[keyMatch];
    if (!selectedValues) return filters;
    const isObjectList = typeof filters[0] === "object";
    return filters.filter((f) => selectedValues.includes(isObjectList ? f.key : f));
  };

  const getStaticOptionsView = (filters: any, parent: any) => {
    return filters?.map((subFilter: any) => {
      const isChecked = checkedList.length
        ? checkedList
            .find((checkedList: any) => checkedList.key === parent.key)
            ?.options.indexOf(getChildernValueToCheckIsChecked(parent?.optionsType, subFilter)) > -1
        : false;

      return (
        <Box
          key={uniqueId("SUBFILTER_")}
          sx={{
            ...{
              paddingLeft: "50px",
              color: tokens.Text.General.PrimaryHeadline
            },
            ...{}
          }}
        >
          <FormControlLabel
            label={
              <Typography variant="body2">
                {optionTypeArray.includes(parent?.optionsType) ? subFilter?.value : subFilter}
              </Typography>
            }
            control={
              [optionsTypes["static-single-select-key-value"], optionsTypes["static-single-select-array"]].includes(
                parent?.optionsType
              ) ? (
                <Radio
                  disabled={disableFields}
                  id={subFilter?.id}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChildrenChange(e, {
                      parentId: parent.id,
                      parentKey: parent.key,
                      optionsType: parent?.optionsType,
                      parentName: parent.name,
                      option: optionTypeArray.includes(parent?.optionsType) ? subFilter?.key : subFilter
                    })
                  }
                  checked={isChecked}
                />
              ) : (
                <Checkbox
                  id={subFilter?.id}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChildrenChange(e, {
                      parentId: parent.id,
                      parentKey: parent.key,
                      optionType: parent?.optionsType,
                      parentName: parent.name,
                      option: optionTypeArray.includes(parent?.optionsType) ? subFilter?.key : subFilter
                    })
                  }
                  checked={isChecked}
                />
              )
            }
          />
        </Box>
      );
    });
  };

  const nestedOptionViewer = (parent: any) => {
    const isChecked = (filterList: any[], key: string) => {
      return checkedList.some(
        (checked: any) => checked.key === key && filterList?.every((filter) => checked?.options?.includes(filter?.id))
      );
    };

    return parent.filters
      ?.filter((filter: any) => (functionalAreaId ? filter?.functionalAreaIds?.includes(functionalAreaId) : true))
      .map((subFilter: any) => {
        const isSubFilterChecked = isChecked(subFilter?.filters, parent.key);
        return (
          <Box
            key={subFilter.id}
            sx={{
              paddingLeft: "50px",
              color: tokens.Text.General.PrimaryHeadline
            }}
          >
            <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} paddingRight={"25px"}>
              <FormControlLabel
                label={
                  <Typography variant="body2">
                    {optionTypeArray.includes(parent?.optionsType) ? subFilter?.value : subFilter?.name}
                  </Typography>
                }
                control={
                  <CheckBox
                    id={subFilter?.id}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChildrenChange(e, {
                        parentId: parent.id,
                        parentKey: parent.key,
                        optionType: parent?.optionsType,
                        parentName: parent.name,
                        option: subFilter?.filters?.map((item: any) => item?.id)
                      })
                    }
                    checked={isSubFilterChecked}
                  />
                }
              />
              <IconButton aria-label="expand" onClick={() => handleChange(subFilter.id, parent.key)}>
                {expanded.indexOf(subFilter.id) > -1 ? <ArrowUp /> : <ArrowDown />}
              </IconButton>
            </Box>
            <CollapsibleContainer
              style={{ maxWidth: isMobileScreen ? "100%" : "350px" }}
              isOpen={expanded.indexOf(subFilter.id) > -1}
            >
              <Box>
                {subFilter.filters?.map((item: any) => {
                  const isItemChecked = isChecked([item], parent.key);

                  return (
                    <Box key={item.id} sx={{ paddingLeft: "40px" }}>
                      <FormControlLabel
                        label={<Typography variant="body2">{item?.name}</Typography>}
                        control={
                          <CheckBox
                            id={item?.id}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleChildrenChange(e, {
                                parentId: parent.id,
                                parentKey: parent.key,
                                optionType: parent?.optionsType,
                                parentName: parent.name,
                                option: [item?.id]
                              })
                            }
                            checked={isItemChecked}
                          />
                        }
                      />
                    </Box>
                  );
                })}
              </Box>
            </CollapsibleContainer>
          </Box>
        );
      });
  };

  const handleDateRangeSelect = (data: any, parent: any) => {
    const f = (d: Date) =>
      d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    const range = `${f(data.startDate)}${data.endDate ? ` To ${f(data.endDate)}` : ""}`;
    setCheckedList((list: any) =>
      list.some((i: any) => i.key === parent.key)
        ? list.map((i: any) => (i.key === parent.key ? { ...i, options: [range] } : i))
        : [
            ...list,
            {
              id: parent.id,
              name: parent.name,
              key: parent.key,
              options: [range]
            }
          ]
    );
  };

  const getChildrenCheckboxes = (parent: any, isChecked: boolean) => {
    let filters = [];
    if (parent?.optionsType === "2-level-nested-filter-categories") {
      return nestedOptionViewer(parent);
    } else if (parent?.optionsType === "date-range") {
      const dateRange = checkedList?.find((checkedList: any) => checkedList.key === parent.key)?.options?.[0];
      const [start, end] = (dateRange?.split(" To ") ?? ["", ""]).map((s: string) => s.trim());
      return (
        <DateRangePickerCalendar
          dateRangeValue={{
            startDate: start ? new Date(start) : null,
            endDate: end ? new Date(end) : null
          }}
          parent={parent}
          onChange={handleDateRangeSelect}
        />
      );
    } else if (parent?.optionsType === "key-value-array-local") {
      filters = getDynamicFiltersOptions(parent?.key) || [];
    } else {
      filters = parent?.filters || [];
    }

    return (
      <Box>
        {parent?.isDynamic === "Y" ? (
          <Box display={"flex"} flexDirection={"column"} gap={"10px"} paddingX={"17px"}>
            <Box padding={"10px"} display={"flex"} gap="20px" flexDirection={"column"}>
              <SearchAppBar
                isPreDefinedNav={true}
                value={getDynamicView(parent?.key, "value")}
                getInputDetails={(e: any) => {
                  const fun: any = getDynamicView(parent?.key, "change");
                  fun(e);
                }}
                planType={"filter"}
                sx={{ border: "1px solid", borderColor: tokens.Border.General.Tertiary }}
              ></SearchAppBar>
              {isChecked && <Typography variant="label">{"All items selected, too many to display."}</Typography>}
            </Box>

            {!getDynamicView(parent?.key, "value") &&
              getSelectedDynamicCategoryResult(
                getKeyCheck(parent?.key, corpFilterTypeConst.innCode),
                getKeyCheck(parent?.key, corpFilterTypeConst.members),
                getKeyCheck(parent?.key, corpFilterTypeConst.owners),
                parent
              )}
            {getDynamicCategoryResult(
              getKeyCheck(parent?.key, corpFilterTypeConst.innCode),
              getKeyCheck(parent?.key, corpFilterTypeConst.members) ||
                getKeyCheck(parent?.key, corpFilterTypeConst.owners),
              getDynamicView(parent?.key, "value"),
              parent,
              getDynamicView(parent?.key, "result")
            )}
          </Box>
        ) : (
          getStaticOptionsView(getFiltersByViewType(filters, parent), parent)
        )}
      </Box>
    );
  };

  const handleChildrenChange = (event: React.ChangeEvent<HTMLInputElement>, data: any) => {
    const checked =
      event.target.checked ||
      [
        optionsTypes["dynamic-single-select"],
        optionsTypes["static-single-select-key-value"],
        optionsTypes["static-single-select-array"]
      ].includes(data.optionsType);
    let newCheckedList = cloneDeep(checkedList);
    const foundItemIndex = newCheckedList.findIndex((item: any) => item.key === data?.parentKey);
    if (checked) {
      if (foundItemIndex !== -1) {
        newCheckedList[foundItemIndex].options = ["key-array-value-array", "2-level-nested-filter-categories"].includes(
          data?.optionType
        )
          ? Array.from(new Set([...newCheckedList[foundItemIndex].options, ...(data?.option || [])]))
          : [
              optionsTypes["dynamic-single-select"],
              optionsTypes["static-single-select-key-value"],
              optionsTypes["static-single-select-array"]
            ].includes(data.optionsType)
          ? Array.from(new Set([data?.option]))
          : Array.from(new Set([...newCheckedList[foundItemIndex].options, data?.option]));
      } else {
        newCheckedList.push({
          id: data?.parentId,
          options: ["key-array-value-array", "2-level-nested-filter-categories"].includes(data?.optionType)
            ? [...(data?.option || [])]
            : [data?.option],
          key: data?.parentKey,
          name: data?.parentName
        });
      }
    } else {
      let count: number = 0;
      newCheckedList[foundItemIndex].options = newCheckedList[foundItemIndex].options?.filter((item: any) =>
        ["key-array-value-array", "2-level-nested-filter-categories"].includes(data?.optionType)
          ? !data?.option?.includes(item)
          : item !== data?.option
      );
      count = newCheckedList[foundItemIndex].options?.length;
      if (count === 0) newCheckedList.splice(foundItemIndex, 1);
    }
    if (page === filterPage.libraryPlanDetailsDisplay && getKeyCheck(data?.parentKey, corpFilterTypeConst.innCode)) {
      newCheckedList = newCheckedList?.filter((item: any) => getKeyCheck(item?.key, corpFilterTypeConst.innCode));
      setDisableFields(true);
    }
    setCheckedList(newCheckedList);
    if (getKeyCheck(data?.parentKey, corpFilterTypeConst.innCode) && checked)
      dispatch(setCheckedDynamicInncode({ innCode: data?.option, propName: data?.name }));
    else if (getKeyCheck(data?.parentKey, corpFilterTypeConst.planName) && checked)
      dispatch(setCheckedPlanInfo({ id: data?.option, name: data?.name }));
    else if (getKeyCheck(data?.parentKey, corpFilterTypeConst.owners) && checked)
      dispatch(setCheckedOwnerInfo({ id: data?.option, name: data?.name, email: data?.email }));
    else if (getKeyCheck(data?.parentKey, corpFilterTypeConst.members) && checked)
      dispatch(setMemberInfo({ hltId: data?.option, displayName: data?.name, email: data?.email }));
    else if (getKeyCheck(data?.parentKey, corpFilterTypeConst.property) && checked)
      dispatch(setStaticPropertyInfo({ id: data?.option, name: data?.name }));
  };

  const getOptions = () => (
    <Box
      key={"options"}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%"
      }}
    >
      <Box minWidth={"350px"} maxWidth={"470px"} role="presentation">
        {
          <Box
            sx={{
              position: "sticky",
              top: 0,
              padding: "25px 25px 25px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",
              zIndex: 1
            }}
          >
            <Typography variant={"h5"} showBold color={tokens.Text.General.Secondary}>
              Filters
            </Typography>

            <Typography sx={{ marginLeft: "8px" }} variant="h5" color={tokens.Text.General.Secondary}>
              (
              {checkedList?.reduce(
                (total: number, obj: { options: string | any[] }) =>
                  total + (Array.isArray(obj.options) ? getGlobalLevelFilterCount(obj) : 0),
                0
              )}
              )
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", marginLeft: "auto" }}>
              {isDefaultButtonsApplicable && !checkForFilterChangedFromModified(checkedList) && (
                <IconButton
                  sx={{ width: "60%" }}
                  onClick={() => {
                    setCheckedList(globalFiltersforPage.defaultFilter);
                  }}
                >
                  <FilterIconBlue />
                </IconButton>
              )}

              <IconButton onClick={toggleInput(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        }
        <Box>{getParentCheckBoxes()}</Box>
      </Box>
      {getFooter()}
    </Box>
  );

  const getAllKeysFromPage = (pageName: string, data: any) => {
    return data?.filter((item: any) => pageName in item).map((item: any) => item.key);
  };

  const filterByKeys = (data: any, keys: any) => {
    return data?.filter((item: any) => keys?.includes(item.key));
  };

  const getFooter = () => {
    return (
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "space-evenly",
          flexDirection: "column",
          padding: "20px",
          background: "white"
        }}
      >
        <Button
          variant="primary"
          label="Apply"
          disabled={isGlobalCheckBoxApplicable ? !isGlobalCheckBoxApplicable : !checkedList?.length}
          sx={{ width: "100%" }}
          onClick={() => {
            dispatch(
              setPageFilter({
                pageName: page,
                modifiedFilter: checkedList,
                isFilterApplied: true
              })
            );
            setOpenDrawer(false);

            if (applyFilterToMultiplePages?.length) {
              applyFilterToMultiplePages.map((pageName: any) => {
                const filterKeys = getAllKeysFromPage(pageName, filterCategories);
                const filterCheckList = filterByKeys(checkedList, filterKeys);
                dispatch(setPageFilter({ pageName, modifiedFilter: filterCheckList }));
              });
            }

            if (page === filterPage.consultantInncodePlansDisplay)
              dispatch(updateBookmarkedInncodesAsync({ bookmarks: bookmarked, shouldUpdateState: true }));
          }}
        />
        {isGlobalCheckBoxApplicable && (
          <Button
            variant="secondary"
            label="Clear All"
            disabled={!checkedList?.length}
            sx={{ width: "100%" }}
            onClick={() => setCheckedList([])}
          />
        )}
      </Box>
    );
  };

  const getInputDetails = (input: any) => {
    if (input.length > minSearchStrLength || !input) {
      dispatch(
        setPageFilter({
          pageName: page,
          searchText: input,
          isFilterApplied: globalFiltersforPage?.isFilterApplied
        })
      );
      dispatch(setSearchText(input));
    }
  };

  const getDrawerLayout = () => {
    const selectedCount = selectedFilter;

    return (
      <>
        <Box className="filters-container">
          {isSearchBarVisible && (
            <Box className="search-panel">
              <SearchAppBar
                isPreDefinedNav={isPreDefinedNav}
                className="search-app-bar"
                value={searchText}
                getInputDetails={getInputDetails}
                placeholder={searchBarPlaceholder}
                sx={{ border: `solid 1px ${tokens.Border.General.Tertiary}` }}
                libraryRecentSearch={props.libraryRecentSearch}
                setRecentSearch={setRecentSearch}
              />
            </Box>
          )}

          <Box className="filter-panel">
            {selectedCount?.length > 0 && !isDefaultButtonsApplicable && (
              <ButtonLink
                text="Clear all"
                sx={{ color: tokens.Text.General.Color, fontWeight: "700", textDecoration: "underline" }}
                textVariant="body2"
                onClick={() => dispatch(resetPageModifiedFilter({ pageName: page }))}
              />
            )}
            {!checkForFilterChangedFromModified(globalFiltersforPage?.modifiedFilter) &&
            isDefaultButtonsApplicable &&
            globalFiltersforPage?.isFilterApplied ? (
              <>
                {saveAsDefaultApplicable() && (
                  <ButtonLink
                    text="Save as Default"
                    sx={{ color: tokens.Text.General.Color, fontWeight: "700", textDecoration: "underline" }}
                    textVariant="body2"
                    onClick={() => {
                      setIsGlobalCheckBoxSelect(false);
                      updateSavedFilter();
                    }}
                    accessType={isImpersonated ? accessTypes.delete : ""}
                  />
                )}
                {isMobileScreen ? (
                  <IconButton
                    onClick={() =>
                      dispatch(setPageFilter({ pageName: page, modifiedFilter: [], isFilterApplied: false }))
                    }
                    style={{ border: "1px solid #007293", width: "44px", height: "44px" }}
                    color="secondary"
                  >
                    <FilterIconBlueMobile />
                  </IconButton>
                ) : (
                  <Button
                    onClick={() => {
                      dispatch(setPageFilter({ pageName: page, modifiedFilter: [], isFilterApplied: false }));
                      setIsGlobalCheckBoxSelect(false);
                    }}
                    variant="secondary"
                    label={`Reset to Default`}
                  />
                )}
              </>
            ) : (
              <></>
            )}
            {isMobileScreen ? (
              <IconButton
                onClick={toggleInput(true)}
                style={{ backgroundColor: tokens.Icon.General.Color, width: "44px", height: "44px" }}
              >
                <Filter />
              </IconButton>
            ) : (
              <Button
                onClick={toggleInput(true)}
                startIcon={<Filter />}
                variant="primary"
                label={filterButtonName}
                endIcon={
                  selectedCount?.length > 0 &&
                  isCountVisibleOnButton(selectedCount) && (
                    <Box>
                      <Typography
                        className="count-badge"
                        sx={{
                          backgroundColor: colors.Blue.Blue200,
                          paddingX: "8px",
                          borderRadius: "200px"
                        }}
                        variant={"body2"}
                        showBold
                      >
                        {selectedCount?.reduce(
                          (total: any, obj: { options: string | any[] }) =>
                            total + (Array.isArray(obj.options) ? getGlobalLevelFilterCount(obj) : 0),
                          0
                        )}
                      </Typography>
                    </Box>
                  )
                }
              />
            )}
          </Box>
          <Drawer
            anchor={"right"}
            open={openDrawer}
            onClose={toggleInput(false)}
            PaperProps={{
              sx: {
                justifyContent: "space-between",
                width: { xs: "100%", md: "auto" }
              }
            }}
          >
            {getOptions()}
          </Drawer>
        </Box>
      </>
    );
  };

  return getDrawerLayout();
});

export default GlobalFilters;
